
import { useState, useEffect, useMemo } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Pencil, Trash2, Loader2, Calendar, DollarSign } from "lucide-react"
import { toast } from "sonner"
import { deleteTransaction, updateTransaction, formatTransactionDate } from "@/lib/services/transactions"
import type { Transaction } from "@/lib/types"

interface TransactionDetailProps {
  transaction: Transaction | null
  open: boolean
  onClose: () => void
  onUpdate: () => void
}

export function TransactionDetail({ transaction, open, onClose, onUpdate }: TransactionDetailProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  // Form state
  const [amount, setAmount] = useState("")
  const [description, setDescription] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("")
  const [date, setDate] = useState("")

  const expenseCategories = [
    { name: "Food", icon: "🍔", color: "bg-chart-3/10 text-chart-3" },
    { name: "Transport", icon: "🚗", color: "bg-chart-2/10 text-chart-2" },
    { name: "Shopping", icon: "🛍️", color: "bg-chart-1/10 text-chart-1" },
    { name: "Entertainment", icon: "🎬", color: "bg-chart-5/10 text-chart-5" },
    { name: "Health", icon: "💪", color: "bg-chart-4/10 text-chart-4" },
    { name: "Bills", icon: "📄", color: "bg-primary/10 text-primary" },
    { name: "Other", icon: "📦", color: "bg-muted text-muted-foreground" },
  ]

  const incomeCategories = [
    { name: "Freelance", icon: "💼", color: "bg-chart-1/10 text-chart-1" },
    { name: "Business", icon: "🏢", color: "bg-chart-2/10 text-chart-2" },
    { name: "Investment", icon: "📈", color: "bg-chart-3/10 text-chart-3" },
    { name: "Bonus", icon: "🎁", color: "bg-chart-4/10 text-chart-4" },
    { name: "Gift", icon: "🎉", color: "bg-chart-5/10 text-chart-5" },
    { name: "Other", icon: "💰", color: "bg-success/10 text-success" },
  ]

  const currentCategories = useMemo(() => {
    return transaction?.type === "expense" ? expenseCategories : incomeCategories
  }, [transaction?.type])

  // Initialize form when transaction changes
  useEffect(() => {
    if (transaction) {
      setAmount(Math.abs(transaction.amount).toString())
      setDescription(transaction.name)
      setSelectedCategory(transaction.category)
      setDate(new Date(transaction.date).toISOString().split("T")[0])
      setIsEditing(false)
    }
  }, [transaction])

  const handleEdit = () => {
    setIsEditing(true)
  }

  const handleCancel = () => {
    if (transaction) {
      setAmount(Math.abs(transaction.amount).toString())
      setDescription(transaction.name)
      setSelectedCategory(transaction.category)
      setDate(new Date(transaction.date).toISOString().split("T")[0])
    }
    setIsEditing(false)
  }

  const handleSave = async () => {
    if (!transaction) return

    // Validation
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("Please enter a valid amount")
      return
    }

    if (!selectedCategory) {
      toast.error("Please select a category")
      return
    }

    setIsSaving(true)

    try {
      await updateTransaction(transaction.id, transaction.type, {
        name: description || `${selectedCategory} ${transaction.type}`,
        amount: parseFloat(amount),
        category: selectedCategory,
        created_at: new Date(date).toISOString(),
      })

      toast.success(`${transaction.type === "expense" ? "Expense" : "Income"} updated successfully!`)
      setIsEditing(false)
      onUpdate()
    } catch (error) {
      console.error("Error updating transaction:", error)
      toast.error("Failed to update transaction. Please try again.")
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!transaction) return

    setIsDeleting(true)

    try {
      await deleteTransaction(transaction.id, transaction.type)
      toast.success(`${transaction.type === "expense" ? "Expense" : "Income"} deleted successfully!`)
      setShowDeleteConfirm(false)
      onClose()
      onUpdate()
    } catch (error) {
      console.error("Error deleting transaction:", error)
      toast.error("Failed to delete transaction. Please try again.")
    } finally {
      setIsDeleting(false)
    }
  }

  if (!transaction) return null

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[540px] max-h-[90vh] overflow-y-auto bg-white dark:bg-black border border-white/20 dark:border-white/10 shadow-2xl shadow-black/10">
          <DialogHeader className="space-y-2 sm:space-y-3 pt-2 sm:pt-3">
            <div className="flex items-center justify-between gap-2">
              <DialogTitle className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text">
                {isEditing ? "Edit Transaction" : "Transaction Details"}
              </DialogTitle>
              {/* Type Badge */}
              <div
                className={`px-3 py-1 sm:px-4 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-semibold backdrop-blur-md border transition-all whitespace-nowrap ${
                  transaction.type === "income"
                    ? "bg-income/20 border-income/30 text-income shadow-lg shadow-income/10"
                    : "bg-expense/20 border-expense/30 text-expense shadow-lg shadow-expense/10"
                }`}
              >
                {transaction.type === "income" ? "💰 Income" : "💸 Expense"}
              </div>
            </div>
            <DialogDescription className="text-xs sm:text-sm text-muted-foreground">
              {isEditing ? "Update the transaction information below" : `View and manage your ${transaction.type} details`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 sm:space-y-5 py-3 sm:py-4">
            {/* Amount Display - Glassmorphic Hero Card */}
            <div
              className={`relative overflow-hidden rounded-2xl p-4 sm:p-6 transition-all duration-300 ${
                transaction.type === "income"
                  ? "bg-gradient-to-br from-income/20 via-income/10 to-income/5 border border-income/20"
                  : "bg-gradient-to-br from-expense/20 via-expense/10 to-expense/5 border border-expense/20"
              } shadow-xl`}
            >
              {/* Animated gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/5 to-white/0 opacity-50" />

              <div className="relative space-y-2 sm:space-y-3">
                <div className="flex items-center gap-2 text-xs sm:text-sm font-medium text-muted-foreground">
                  <DollarSign className="h-3 w-3 sm:h-4 sm:w-4" />
                  <Label htmlFor="amount" className="text-xs sm:text-sm font-medium">Amount</Label>
                </div>

                {isEditing ? (
                  <div className="relative">
                    <span className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-xl sm:text-2xl font-bold text-muted-foreground">
                      $
                    </span>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="pl-10 sm:pl-12 text-xl sm:text-2xl md:text-3xl font-bold h-12 sm:h-16 bg-white/50 dark:bg-white/5 border-white/30 focus:border-white/50 transition-all"
                      disabled={isSaving}
                    />
                  </div>
                ) : (
                  <div className={`text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight ${
                    transaction.amount > 0 ? "text-income" : "text-expense"
                  }`}>
                    {transaction.amount > 0 ? "+" : "-"}${Math.abs(transaction.amount).toFixed(2)}
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons - View Mode */}
            {!isEditing && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 backdrop-blur-md bg-white/60 dark:bg-white/5 border-white/20 hover:bg-white/80 dark:hover:bg-white/10 transition-all shadow-sm"
                  onClick={handleEdit}
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1 backdrop-blur-md bg-gradient-to-r from-red-500/80 to-red-600/80 hover:from-red-600/90 hover:to-red-700/90 border border-red-500/30 shadow-lg shadow-red-500/20 transition-all"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </div>
            )}

            {/* Description */}
            <div className="space-y-2 bg-white/40 dark:bg-white/5 rounded-xl p-3 sm:p-4 border border-white/20">
              <Label htmlFor="description" className="text-xs sm:text-sm font-medium flex items-center gap-2">
                📝 Description
              </Label>
              {isEditing ? (
                <Input
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What was this for?"
                  className="bg-white/60 dark:bg-white/5 border-white/30 focus:border-white/50 text-sm"
                  disabled={isSaving}
                />
              ) : (
                <p className="text-sm sm:text-base font-medium">{transaction.name}</p>
              )}
            </div>

            {/* Category */}
            <div className="space-y-2 sm:space-y-3 bg-white/40 dark:bg-white/5 rounded-xl p-3 sm:p-4 border border-white/20">
              <Label className="text-xs sm:text-sm font-medium flex items-center gap-2">
                🏷️ Category
              </Label>
              {isEditing ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {currentCategories.map((category) => (
                    <button
                      key={category.name}
                      onClick={() => setSelectedCategory(category.name)}
                      disabled={isSaving}
                      className={`group flex flex-col items-center gap-1 sm:gap-2 p-2 sm:p-3 rounded-xl transition-all duration-200 ${
                        selectedCategory === category.name
                          ? "ring-2 ring-primary/50 bg-white/90 dark:bg-white/20 scale-105 shadow-lg"
                          : "bg-white/60 dark:bg-white/5 hover:bg-white/80 dark:hover:bg-white/10 hover:scale-105"
                      } ${isSaving ? "opacity-50 cursor-not-allowed" : "cursor-pointer"} border border-white/20`}
                    >
                      <div className={`h-8 w-8 sm:h-10 sm:w-10 rounded-lg ${category.color} flex items-center justify-center text-lg sm:text-xl transition-transform group-hover:scale-110`}>
                        {category.icon}
                      </div>
                      <span className="text-[10px] sm:text-xs font-medium text-center leading-tight">{category.name}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-xl bg-white/60 dark:bg-white/10 border border-white/20">
                  <div className={`h-10 w-10 sm:h-14 sm:w-14 rounded-xl ${transaction.color} flex items-center justify-center text-xl sm:text-2xl shadow-md`}>
                    {transaction.icon}
                  </div>
                  <span className="text-base sm:text-lg font-semibold">{transaction.category}</span>
                </div>
              )}
            </div>

            {/* Date */}
            <div className="space-y-2 bg-white/40 dark:bg-white/5 rounded-xl p-3 sm:p-4 border border-white/20">
              <Label htmlFor="date" className="text-xs sm:text-sm font-medium flex items-center gap-2">
                <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                Date
              </Label>
              {isEditing ? (
                <Input
                  id="date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="bg-white/60 dark:bg-white/5 border-white/30 focus:border-white/50 text-sm"
                  disabled={isSaving}
                />
              ) : (
                <p className="text-sm sm:text-base font-medium">{formatTransactionDate(transaction.date)}</p>
              )}
            </div>
          </div>

          {/* Action Buttons - Edit Mode */}
          {isEditing && (
            <div className="flex justify-end gap-2 pt-4 border-t border-white/20">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={isSaving}
                className="backdrop-blur-md bg-white/60 dark:bg-white/5 border-white/20 hover:bg-white/80 dark:hover:bg-white/10"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaving}
                className="backdrop-blur-md bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary shadow-lg"
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Save Changes
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent className="backdrop-blur-xl bg-white/90 dark:bg-black/60 border border-white/20 dark:border-white/10 shadow-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl">Delete Transaction?</AlertDialogTitle>
            <AlertDialogDescription className="text-base">
              Are you sure you want to delete this {transaction.type}? This action cannot be undone and will permanently remove this transaction from your records.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={isDeleting}
              className="backdrop-blur-md bg-white/60 dark:bg-white/5 border-white/20 hover:bg-white/80 dark:hover:bg-white/10"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="backdrop-blur-md bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 border border-red-500/30 shadow-lg shadow-red-500/20"
            >
              {isDeleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
